'use client';
import { useWindowOpen } from '@repo/platform';
import { BoxButton, Divider, useWindowSize } from '@repo/ui';
import dynamic from 'next/dynamic';
import { CSSProperties, Fragment, useMemo } from 'react';

const LottieWebPlayer = dynamic(() => import('@repo/ui').then((m) => ({ default: m.LottieWebPlayer })), { ssr: false });

import { CONTENT_FEATURES } from '@/lib/constants/landing-sections';
import { ContentFeature } from '@/lib/types/landing';

export const ContentSection = () => {
  return (
    <div className="w-full flex flex-col items-center py-0 lg:py-spacious-small">
      {CONTENT_FEATURES.map((feature, index) => (
        <Fragment key={index}>
          <FeatureItem {...feature} />
          <Divider size="large" className={'w-full block lg:hidden bg-neutral-background-main'} />
        </Fragment>
      ))}
    </div>
  );
};

const FeatureItem = ({ logoUrl, logoHeight = '0.75em', backgroundClassName, title, description, buttonText, buttonLink, lottiePath }: ContentFeature) => {
  const { openWindow } = useWindowOpen();
  return (
    <section
      className={`container-xlarge container-padding-large lg:container-padding-xlarge pt-spacious-large lg:pt-huge-medium pb-0 lg:pb-huge-medium w-full flex flex-col items-center overflow-hidden lg:overflow-visible lg:bg-none lg:bg-white ${backgroundClassName ?? ''}`}
    >
      <div
        className={`w-full flex items-center flex-col-reverse lg:flex-row-reverse gap-spacious-small lg:gap-spacious-large justify-between container-small lg:max-w-none`}
      >
        <div className={`flex-auto flex items-center justify-center mb-[-22.5%] lg:mb-0`}>
          <MockUp lottiePath={lottiePath} />
        </div>
        <div
          className={`flex-auto text-center lg:text-left container-small xs:heading-medium-bold heading-large-bold lg:max-w-container-xsmall py-normal-large`}
        >
          <img src={logoUrl} alt="feature logo" style={{ height: logoHeight }} className={'lg:w-fit w-full pl-tiny-medium mb-normal-medium'} />
          <h2 className={'text-neutral-foreground-main leading-heading'}>{title}</h2>
          <p className={'text-neutral-foreground-lowest mt-normal-medium lg:mt-normal-large mb-0 body-large-semibold lg:heading-small-semibold'}>
            {description}
          </p>
          <BoxButton
            size={'large'}
            variant={'filled-primary'}
            className={'rounded-full mt-spacious-small'}
            onClick={() => {
              openWindow(buttonLink);
            }}
          >
            {buttonText}
          </BoxButton>
        </div>
      </div>
    </section>
  );
};

type ImageProps = {
  lottiePath: string;
};

export const MockUp = ({ lottiePath }: ImageProps) => {
  const { isOverMobile, isOverTablet } = useWindowSize();
  const { width, height } = useMemo(() => {
    if (isOverTablet) return { width: 277.74, height: 600 };
    if (isOverMobile) return { width: 231.45, height: 500 };
    return { width: 203.67, height: 440 };
  }, [isOverMobile, isOverTablet]);

  // frame 위치 및 크기 계산
  const frameWidth = 944;
  const frameHeight = 1384;
  const frameLeft = 32;
  const frameTop = 23;
  const screenWidth = 524;
  const screenHeight = 1132;

  const frameStyle: CSSProperties = {
    position: 'absolute',
    zIndex: 20,
    margin: '0 auto',
    top: `calc(-1px * ${height} * ${frameTop} / ${screenHeight})`,
    left: `calc(-1px * ${width} * ${frameLeft} / ${screenWidth})`,
    width: `calc(100% * (${frameWidth} / ${screenWidth}))`,
    height: `calc(100% * (${frameHeight} / ${screenHeight}))`,
    lineHeight: 0
  };

  return (
    <div className={'relative pointer-events-none leading-none w-[203.67px] h-[440px] sm:w-[231.45px] sm:h-[500px] md:w-[277.74px] md:h-[600px]'}>
      <div className={'absolute mx-auto z-20 leading-[0]'} style={frameStyle}>
        <img src="/images/iPhone16-frame.png" className={'w-full h-full'} alt="목업용 이미지" />
      </div>
      <div className="absolute left-0 top-0 w-full h-full bg-white leading-none">
        <LottieWebPlayer src={lottiePath} />
      </div>
    </div>
  );
};
